<template>
  <div class="impact-page">
    <impact-header title="指标口径" :tags="['口径说明', '先对齐定义再看图']" :chips="chips" loaded-at="静态说明，随口径版本更新"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心设计原则</h2>
      </div>
      <p class="block-lead">这几条决定了看板上每个数字怎么算、为什么这么算。</p>
      <div class="grid g2">
        <div class="card">
          <div class="note">
            <b>1. 三类事件统一归到「换老师」，不拆链路。</b>离职、淘汰、关闭时间对学生的结果一样——原来那位老师上不了了，得换人。敞口口径统一为未消课时；类型之间的差异只落在<b>波及范围</b>（离职 / 淘汰按老师全部学生，关闭时间只看原时段落在关闭区间的学生）和<b>通知窗口</b>上。
          </div>
          <div class="note is-warning">
            <b>2. 学生是归因主键，不是老师。</b>一个学生可能同时被多位老师波及，按老师计数会重复。窗口内同一学生被多起事件波及，只归因到生效日最早的一起，其余计「重复受影响」。
          </div>
          <div class="note is-danger">
            <b>3. 真正的分水岭是保时换还是改时换。</b>同样换老师，保住原时段和被迫改时间的 30 天留存差距，比「属于哪种异动类型」更能解释留存。
          </div>
          <div class="note is-healthy">
            <b>4. 结果指标必须带对照组。</b>异动后 30 天留存单独看没有意义，要和同期未被波及的活跃学生比，差值才是真实影响。
          </div>
          <div class="note">
            <b>5. 3 小时内不代课，这个窗口单独看。</b>3 小时内的课节不安排代课，不能和 3 小时外混在一起看代课率，否则整体指标会被 3 小时外拉平，看不出无人兜底的那部分。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">范围与基础口径</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>项</th><th>口径</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in baseRows" :key="row.name">
                <td class="t-name">{{ row.name }}</td>
                <td class="wrap">{{ row.desc }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">异动事件与波及学生</h2>
      </div>
      <p class="block-lead">「影响总览」「学生异动影响」两页的事件、波及和事件后结果口径。</p>
      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">三类异动事件</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>类型</th><th>来源</th><th>生效日</th><th>通知天数</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in eventRows" :key="row.type">
                <td><span class="badge" :class="row.badge">{{ row.label }}</span></td>
                <td class="wrap">{{ row.source }}</td>
                <td class="wrap">{{ row.date }}</td>
                <td class="wrap">{{ row.notice }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="note">离职老师生效日为空不进统计；通知天数小于 0 记 0。</div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">波及与事件后结果</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>指标</th><th>口径</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in changeRows" :key="row.name">
                <td class="t-name">{{ row.name }}</td>
                <td class="wrap">{{ row.desc }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">请假 / 旷课与学生汇总</h2>
      </div>
      <p class="block-lead">「请假/旷课影响」「学生影响明细」「综合风险视图」三页的口径。</p>
      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">请假 / 旷课（课节维度）</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>指标</th><th>口径</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in leaveRows" :key="row.name">
                <td class="t-name">{{ row.name }}</td>
                <td class="wrap">{{ row.desc }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">学生事件、状态与风险分</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>指标</th><th>口径</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in studentRows" :key="row.name">
                <td class="t-name">{{ row.name }}</td>
                <td class="wrap">{{ row.desc }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">待确认口径</h2>
      </div>
      <p class="block-lead">一期已按下面的「当前处理」上线，业务拍板后再调整。</p>
      <div class="grid">
        <div class="card">
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr><th>问题</th><th>当前处理</th><th>待拍板</th></tr>
              </thead>
              <tbody>
              <tr v-for="row in pendingRows" :key="row.name">
                <td class="t-name wrap">{{ row.name }}</td>
                <td class="wrap">{{ row.now }}</td>
                <td class="wrap">{{ row.todo }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'

export default {
  name: 'TchImpactModel',
  components: { ImpactHeader },
  data () {
    return {
      chips: [
        { label: '时区', value: 'Asia/Shanghai' },
        { label: '老师', value: '欧美外教' },
        { label: '课节', value: '常规外教课' },
        { label: '比率', value: '分母为 0 显示 —' }
      ],
      baseRows: [
        { name: '时间', desc: '一律 Asia/Shanghai；「今天」= 服务器当日。' },
        { name: '老师范围', desc: '欧美外教（外教且渠道为欧美）；菲教不算。' },
        { name: '常规外教课', desc: '常规课节、课程类型为常规 / 外教常规且有学生的课节。' },
        { name: '完成课', desc: '课节状态为已完成。' },
        { name: '学生 / 班主任', desc: '学生昵称；班主任取学生当前的学导。' },
        { name: '剩余课时', desc: '学生课包常规课时 − 已消常规课时，取当前值，不是事件时点快照。' },
        { name: '开课天数', desc: '事件生效日 − 该生最早一节完成常规外教课日期；≤ 30 天为新生优先。' },
        { name: '比率', desc: '百分数保留 1 位小数；分母为 0 显示「—」。' }
      ],
      eventRows: [
        { type: 'resign', label: '离职', badge: 'is-purple', source: '老师状态为离职（含失联、老记录无类型）', date: '最后工作日', notice: '生效日 − 最新一条离职跟进记录创建日' },
        { type: 'elim', label: '淘汰', badge: 'is-danger', source: '同离职，且该条跟进记录离职类型为淘汰', date: '最后工作日', notice: '同离职' },
        { type: 'close', label: '关闭时间', badge: 'is-warning', source: '老师关闭时间每行一起', date: '关闭日期', notice: '关闭日期 − 提交日期' }
      ],
      changeRows: [
        { name: '波及学生（离职 / 淘汰）', desc: '生效日前 30 天内与该老师有完成常规外教课的学生，去重。' },
        { name: '波及学生（关闭时间）', desc: '同一区间内，与该老师、同星期、课节开始时间落在关闭区间 [开始, 结束) 的完成课学生。' },
        { name: '原时段 / 原周课量', desc: '上述课节里出现最多的（星期, 开始时间）；原周课量 = 不同（星期, 开始时间）个数。' },
        { name: '已换老师 / 换老师天数', desc: '生效日起该生第一节已选常规外教课（lsn_sel_status=1）且老师 ≠ 原老师；天数 = 该课日期 − 生效日。' },
        { name: '待换老师 / 超 7 天', desc: '还没有上述已选新老师课；生效日距今 > 7 天仍待换即超 7 天。' },
        { name: '保时 / 改时', desc: '新老师首课同星期，且开始时间与原时段相差 ≤ 30 分钟为保时，否则改时。' },
        { name: '损失课次', desc: '生效后 14 天应上 = 原周课量 × 2（不满 14 天按已过整周折算），实上 = 该生这段时间任意老师完成课数，损失 = max(0, 应上 − 实上)。' },
        { name: '30 天留存', desc: '生效满 30 天的事件才评估；生效后 30 天内有完成常规外教课即留存。' },
        { name: '对照组', desc: '参考日 R = min(窗口结束日, 今天 − 30)；R 前 30 天有完成课、且 R 前 90 天到 R 后 30 天没被任何异动波及的学生；R 后 30 天有完成课即留存。' }
      ],
      leaveRows: [
        { name: '受影响课节', desc: '常规外教课、范围老师：请假状态（lsn_sel_status=6），或 book_calls 老师缺席标记=1；按课节开始时间落窗口。' },
        { name: '提前小时', desc: '（课节开始时间 − 老师最早一条请假申请时间）÷ 3600。' },
        { name: '分桶', desc: '>24h / 12–24h / 6–12h / 3–6h / 1–3h / <1h / 旷课（考勤缺席）/ 无申请记录（请假状态但查不到申请）。' },
        { name: '3 小时线', desc: '3h 外 = >24h、12–24h、6–12h、3–6h；3h 内 = 1–3h、<1h、旷课；无申请记录只进总量。' },
        { name: '成功代课', desc: '存在以本课节为原课节的代课课节，且状态为已约 / 待上 / 已完成。' },
        { name: '未代课', desc: '= 损失课次。系统里家长取消与改期补回无法区分，结果只分「成功代课 / 未代课」两种。' },
        { name: '请假事件数', desc: '按（老师, 申请时间）去重 + 旷课课节数。' }
      ],
      studentRows: [
        { name: '学生事件', desc: '异动事件（离职 / 淘汰 / 关闭时间）+ 请假课节（3h 内请假、3h 外或无申请记录的请假、旷课，每节 1 次）。' },
        { name: '学生损失课次', desc: '异动损失课次之和 + 未代课的请假课节数。' },
        { name: '当前状态', desc: '流失预警（近 30 天无完成课 且 剩余课时 > 10 且 受影响 ≥ 2 次）→ 待换老师 → 已换老师 → 正常上课，按此优先级取第一个命中。' },
        { name: '主老师', desc: '学生当前固定排课的老师（去重顿号连接）；没有则取最近受影响事件的老师。' },
        { name: '综合风险分', desc: '每起异动 +10，待换 +6，已换但改时 +4；每节请假 +3，3h 内再 +4；两类都有 +7；剩余 ≥ 40 +5、20–39 +3；损失每节 +1；上限 100。' },
        { name: '风险分档', desc: '极高 ≥ 55 / 高 40–54 / 中 25–39 / 低 < 25；学生影响明细「风险」列：极高 / 高 → 高，中 → 中，低 → 低。' },
        { name: '学生异动清单风险', desc: '损失课次 × 3 + 待换 10 + 已换改时 6 + 剩余课时 × 0.3，新生优先 +15 置顶；≥ 25 高 / ≥ 16 中 / 其余低。' },
        { name: '高频被请假学生风险', desc: '被请假次数 × 2 + 3h 内次数 × 3 + 近 7 天有被请假 +4；≥ 15 高 / ≥ 8 中 / 其余低；开课 30 天内被请假 ≥ 2 次为新生优先，置顶。' }
      ],
      pendingRows: [
        { name: '「保时」的容差', now: '同星期且开始时间相差 ≤ 30 分钟算保时，超过算改时。', todo: '阈值直接决定保时率高低，需和运营对齐后固化。' },
        { name: '关闭时间的波及边界', now: '按课节开始时间是否落在关闭区间判定，避免一节课算半次。', todo: '课节跨越关闭边界时是否也算波及。' },
        { name: '改期补回是否算损失', now: '系统无法区分家长取消与改期补回，统一计「未代课」。', todo: '后续若有改约记录，再拆出改期补回单列。' },
        { name: '对照组怎么选', now: '同期未被任何异动波及的活跃学生，未做分层匹配。', todo: '是否按课程类型、剩余课时分位、地区做 1:3 匹配。' },
        { name: '剩余课时口径', now: '取学生当前剩余课时，不是事件时点快照。', todo: '是否需要按事件时点回溯。' },
        { name: '排课策略：先锁时段再配老师', now: '看板只呈现保时 / 改时与留存差异，不改排课逻辑。', todo: '「快」和「稳」的取舍：例如剩余课时 > 20 的学生优先保时，课时少的优先保速度。' }
      ]
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';

.card > .note:first-child {
  margin-top: 0;
}
</style>



// WEBPACK FOOTER //
// TchImpactModel.vue?5aa2cda0